import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from './ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { activitiesApi, leadsApi, aiApi } from '../api/endpoints';
import { toast } from 'sonner';

const typeMeta = {
  lead_created:    { icon: 'UserPlus',     color: 'bg-emerald-50 text-emerald-700' },
  assignment:      { icon: 'UserCheck',    color: 'bg-blue-50 text-blue-700' },
  call:            { icon: 'PhoneCall',    color: 'bg-blue-50 text-blue-700' },
  whatsapp:        { icon: 'MessageCircle',color: 'bg-green-50 text-green-700' },
  remark:          { icon: 'StickyNote',   color: 'bg-amber-50 text-amber-700' },
  followup:        { icon: 'Clock',        color: 'bg-violet-50 text-violet-700' },
  status_change:   { icon: 'GitBranch',    color: 'bg-indigo-50 text-indigo-700' },
  site_visit:      { icon: 'MapPin',       color: 'bg-rose-50 text-rose-700' },
  recording:       { icon: 'Mic',          color: 'bg-slate-50 text-slate-700' },
  ad_call:         { icon: 'Megaphone',    color: 'bg-orange-50 text-orange-700' },
  missed_followup: { icon: 'AlertCircle',  color: 'bg-rose-50 text-rose-700' },
};

const quickStatusOptions = ['New','Contacted','Scheduled Site Visits','Avg Customer Site Visit','Site Visit Completed','Negotiation','Booked','Lost'];

export const buildWhatsappLink = (phone, text) => {
  const clean = (phone || '').replace(/\D/g, '');
  return `https://wa.me/${clean}?text=${encodeURIComponent(text || '')}`;
};
export const buildTelLink = (phone) => `tel:${(phone || '').replace(/\s/g, '')}`;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const LeadActivityDrawer = ({ lead, open, onOpenChange, onLeadUpdated }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [remark, setRemark] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [busy, setBusy] = useState(false);
  const [callOutcome, setCallOutcome] = useState('Connected');
  const [callDuration, setCallDuration] = useState('02:00');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('09:00');
  const [remarkKind, setRemarkKind] = useState('remark');
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (open && lead?.id) {
      setLoading(true);
      activitiesApi.forLead(lead.id).then(setActivities).catch(() => {}).finally(() => setLoading(false));
      setRemark(''); setWhatsapp('');
      setAi(null);
      aiApi.latest(lead.id).then(setAi).catch(() => {});
    }
  }, [open, lead?.id]);

  const generateAi = async () => {
    setAiBusy(true);
    try {
      setAi(await aiApi.generate(lead.id));
      toast.success('AI insights ready');
    } catch (e) {
      console.error('AI insights failed', e);
      toast.error(e?.response?.data?.detail || 'AI generation failed');
    } finally { setAiBusy(false); }
  };

  const refresh = async () => {
    if (!lead) return;
    try { setActivities(await activitiesApi.forLead(lead.id)); }
    catch (e) { console.error('Failed to load lead activities', e); }
  };

  const logRemark = async () => {
    if (!remark.trim()) { toast.error('Type a remark first'); return; }
    setBusy(true);
    try {
      await activitiesApi.add(lead.id, {
        type: remarkKind,
        text: remark.trim(),
        reminder_date: reminderDate || null,
        reminder_time: reminderTime || null,
      });
      setRemark(''); setReminderDate('');
      await refresh();
      const tag = (reminderDate ? ` (reminder set for ${reminderDate} ${reminderTime || ''})` : '');
      toast.success(`${remarkKind === 'followup' ? 'Follow-up' : 'Remark'} added${tag}`);
    } catch (e) { toast.error('Failed to add'); }
    finally { setBusy(false); }
  };

  const logCall = async () => {
    setBusy(true);
    try {
      window.location.href = buildTelLink(lead.phone);
      await activitiesApi.add(lead.id, {
        type: 'call',
        text: `Outbound call — ${callOutcome}`,
        metadata: { outcome: callOutcome, duration: callDuration, direction: 'outbound' },
      });
      await refresh();
      toast.success('Call logged');
    } catch (e) { toast.error('Failed to log call'); }
    finally { setBusy(false); }
  };

  const sendWhatsapp = async () => {
    if (!whatsapp.trim()) { toast.error('Type a message first'); return; }
    setBusy(true);
    try {
      // Open WhatsApp Web for the user to send (deep link)
      window.open(buildWhatsappLink(lead.phone, whatsapp), '_blank');
      // Also trigger backend (mocked AiSensy) which logs activity
      await activitiesApi.whatsapp(lead.id, { type: 'whatsapp', text: whatsapp.trim() });
      setWhatsapp('');
      await refresh();
      toast.success('WhatsApp message sent');
    } catch (e) { toast.error('Failed to send WhatsApp'); }
    finally { setBusy(false); }
  };

  const changeStatus = async (newStatus) => {
    setBusy(true);
    try {
      await activitiesApi.add(lead.id, {
        type: 'status_change',
        text: `Status changed to ${newStatus}`,
        metadata: { status: newStatus },
      });
      await leadsApi.update(lead.id, { status: newStatus });
      onLeadUpdated && onLeadUpdated({ ...lead, status: newStatus });
      await refresh();
      toast.success(`Status → ${newStatus}`);
    } catch (e) { toast.error('Failed to update status'); }
    finally { setBusy(false); }
  };

  const filteredActivities = activities.filter(a => {
    if (tab === 'all') return true;
    if (tab === 'calls') return a.type === 'call' || a.type === 'recording';
    if (tab === 'whatsapp') return a.type === 'whatsapp';
    if (tab === 'remarks') return a.type === 'remark' || a.type === 'followup';
    if (tab === 'system') return ['lead_created','assignment','status_change','site_visit'].includes(a.type);
    return true;
  });

  if (!lead) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col [&>button]:hidden">
        <SheetHeader className="relative p-5 border-b border-slate-200 bg-gradient-to-r from-[#0F1F38] to-[#1B3A5C] text-white">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="drawer-close-btn"
            aria-label="Close panel"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white text-brand-navy hover:bg-brand-gold hover:text-[#0F1F38] shadow-md flex items-center justify-center transition-colors z-10"
          >
            <Icons.X className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <div className="flex items-start gap-3 pr-10">
            <Avatar className="w-12 h-12 border-2 border-brand-gold">
              <AvatarFallback className="bg-white text-brand-navy font-semibold">
                {lead.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-white font-display text-lg truncate">{lead.name}</SheetTitle>
              <SheetDescription className="text-slate-300 text-xs truncate">
                {lead.id} • {lead.property} • Owner: {lead.owner}
              </SheetDescription>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <Badge className="bg-brand-gold text-[#0F1F38] hover:bg-brand-gold border-0">{lead.priority}</Badge>
                <Badge variant="outline" className="border-white/30 text-white bg-white/10">{lead.status}</Badge>
                <span className="text-[11px] text-slate-300 ml-1" data-testid="lead-primary-phone">{lead.phone}</span>
                {lead.alternate_mobile_number ? (
                  <span className="text-[11px] text-slate-300" data-testid="lead-alternate-phone">· Alt: {lead.alternate_mobile_number}</span>
                ) : null}
                {lead.profession ? (
                  <Badge variant="outline" className="border-white/30 text-white bg-white/10" data-testid="lead-profession-badge">
                    {lead.profession}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              onClick={() => { window.open(buildWhatsappLink(lead.phone, ''), '_blank'); }}
            >
              <Icons.MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
            </Button>
            <Button
              size="sm"
              className="bg-brand-gold hover:bg-[#b89252] text-[#0F1F38] font-semibold"
              onClick={logCall}
              disabled={busy}
            >
              <Icons.Phone className="w-4 h-4 mr-1.5" /> Call & Log
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto min-h-0" data-testid="drawer-scroll-body">
        {/* AI Assistant */}
        <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-br from-indigo-50/70 to-white">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs flex items-center gap-1.5 font-semibold text-slate-700">
              <Icons.Sparkles className="w-3.5 h-3.5 text-brand-gold" /> AI Assistant
            </Label>
            <Button size="sm" variant="outline" className="h-7 text-xs border-brand-navy/30 text-brand-navy" onClick={generateAi} disabled={aiBusy} data-testid="ai-generate-btn">
              {aiBusy
                ? <><Icons.Loader2 className="w-3 h-3 mr-1 animate-spin" /> Thinking…</>
                : <><Icons.Sparkles className="w-3 h-3 mr-1" /> {ai ? 'Regenerate' : 'Generate Insights'}</>}
            </Button>
          </div>
          {ai && (
            <div className="mt-2 space-y-2" data-testid="ai-insights-panel">
              <div className="text-xs text-slate-700 bg-white border border-slate-200 rounded-md p-2.5 leading-relaxed" data-testid="ai-summary">{ai.summary}</div>
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2.5 flex gap-2" data-testid="ai-next-action">
                <Icons.Target className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                <span className="text-amber-900"><strong>Next:</strong> {ai.next_action}</span>
              </div>
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md p-2.5" data-testid="ai-draft">
                <div className="text-emerald-900 whitespace-pre-wrap">{ai.whatsapp_draft}</div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid="ai-copy-draft"
                    onClick={() => { navigator.clipboard.writeText(ai.whatsapp_draft); toast.success('Draft copied'); }}>
                    <Icons.Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                  <Button size="sm" className="h-6 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ai-use-draft"
                    onClick={() => { setWhatsapp(ai.whatsapp_draft); toast.success('Draft loaded into WhatsApp composer below'); }}>
                    <Icons.MessageCircle className="w-3 h-3 mr-1" /> Use in WhatsApp
                  </Button>
                </div>
              </div>
              <div className="text-[10px] text-slate-400">Generated {formatTime(ai.generatedAt)} · {ai.model}</div>
            </div>
          )}
        </div>

        <div className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
          <div>
            <Label className="text-xs">Quick Status Change</Label>
            <Select value={lead.status} onValueChange={changeStatus}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {quickStatusOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Add Remark / Follow-up</Label>
            <div className="flex gap-2 mt-1">
              <Select value={remarkKind} onValueChange={setRemarkKind}>
                <SelectTrigger className="h-9 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="remark">Remark</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                </SelectContent>
              </Select>
              <Input value={remark} onChange={e => setRemark(e.target.value)} placeholder="e.g. Customer requested cost sheet..." className="h-9" data-testid="drawer-remark-input" />
              <Button size="sm" onClick={logRemark} disabled={busy} className="bg-brand-navy hover:bg-[#13294A]" data-testid="drawer-remark-save">Add</Button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="text-[10px] uppercase">Remind on</Label>
                <Input type="date" value={reminderDate} onChange={e => setReminderDate(e.target.value)} className="h-8 text-xs" data-testid="drawer-reminder-date" />
              </div>
              <div>
                <Label className="text-[10px] uppercase">Time</Label>
                <Input type="time" value={reminderTime} onChange={e => setReminderTime(e.target.value)} className="h-8 text-xs" data-testid="drawer-reminder-time" />
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Set a date+time — the lead will appear in the Today&apos;s Reminder cards and carry forward daily until you take the next action.</div>
          </div>
          <div>
            <Label className="text-xs">Send WhatsApp Message (auto-logs)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="Type message..." className="h-9" />
              <Button size="sm" onClick={sendWhatsapp} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600">
                <Icons.Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase">Last call outcome</Label>
              <Select value={callOutcome} onValueChange={setCallOutcome}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{['Connected','Voicemail','Interested','Not Interested','Callback'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase">Duration</Label>
              <Input className="h-8 text-xs" value={callDuration} onChange={e => setCallDuration(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="px-4 pt-3 pb-1 border-b border-slate-200">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-5 h-8">
              <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
              <TabsTrigger value="calls" className="text-xs">Calls</TabsTrigger>
              <TabsTrigger value="whatsapp" className="text-xs">WA</TabsTrigger>
              <TabsTrigger value="remarks" className="text-xs">Remarks</TabsTrigger>
              <TabsTrigger value="system" className="text-xs">System</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="p-4 space-y-3">
          {loading && Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
          {!loading && filteredActivities.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-400">No activities in this category yet.</div>
          )}
          {!loading && filteredActivities.map(a => {
            const meta = typeMeta[a.type] || { icon: 'Activity', color: 'bg-slate-50 text-slate-700' };
            const Icon = Icons[meta.icon] || Icons.Activity;
            return (
              <div key={a.id} className="flex gap-3 items-start group">
                <div className={`w-9 h-9 rounded-full ${meta.color} flex items-center justify-center shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 pb-3 border-b border-slate-100">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm font-medium text-slate-800">{a.text}</div>
                    <div className="text-[10px] text-slate-400 shrink-0">{formatTime(a.createdAt)}</div>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    by {a.createdBy} • <span className="capitalize">{a.type.replace(/_/g, ' ')}</span>
                  </div>
                  {a.metadata && a.metadata.outcome && (
                    <Badge variant="outline" className="mt-1 text-[10px]">
                      {a.metadata.outcome}{a.metadata.duration ? ` • ${a.metadata.duration}` : ''}
                    </Badge>
                  )}
                  {a.metadata && a.metadata.reminder && a.metadata.reminder.date && (
                    <Badge variant="outline" className="mt-1 ml-1 text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                      <Icons.AlarmClock className="w-3 h-3 mr-1" />
                      {a.metadata.reminder.date} {a.metadata.reminder.time || ''}
                    </Badge>
                  )}
                  {a.metadata && a.metadata.recording_url && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <audio
                        controls
                        src={a.metadata.recording_url.startsWith('/api')
                          ? `${process.env.REACT_APP_BACKEND_URL || ''}${a.metadata.recording_url}`
                          : a.metadata.recording_url}
                        className="h-7 max-w-[260px]"
                        data-testid={`activity-audio-${a.id}`}
                      />
                      <a
                        href={a.metadata.recording_url.startsWith('/api')
                          ? `${process.env.REACT_APP_BACKEND_URL || ''}${a.metadata.recording_url}`
                          : a.metadata.recording_url}
                        target="_blank" rel="noreferrer"
                        title="Download recording"
                      >
                        <Icons.Download className="w-3.5 h-3.5 text-brand-navy" />
                      </a>
                    </div>
                  )}
                  {a.metadata && a.metadata.preview && (
                    <div className="mt-1 text-xs text-slate-600 bg-slate-50 rounded p-2 italic">“{a.metadata.preview}”</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LeadActivityDrawer;
